/**
 * CircuitBreakerService
 *
 * Implements the circuit breaker pattern to prevent cascading failures
 * and reduce load on external services during outages.
 *
 * Clean Architecture:
 * - Infrastructure concern at system boundary
 * - Use-cases and domain logic remain unaware of circuit breaker
 * - Services wrap operations with circuit breaker protection
 *
 * State Transitions:
 * - CLOSED → OPEN: When failure threshold is reached
 * - OPEN → HALF_OPEN: After reset timeout (with jitter) expires
 * - HALF_OPEN → CLOSED: When success threshold is reached
 * - HALF_OPEN → OPEN: On any failure
 */

import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'circuit-breaker';

/**
 * Circuit breaker states
 */
export enum CircuitState {
  /** Circuit is closed - operations proceed normally */
  CLOSED = 'CLOSED',
  /** Circuit is open - operations are rejected immediately */
  OPEN = 'OPEN',
  /** Circuit is half-open - limited operations allowed to test recovery */
  HALF_OPEN = 'HALF_OPEN',
}

/**
 * Configuration options for the circuit breaker
 */
export interface CircuitBreakerConfig {
  /** Name for logging and identification */
  name: string;
  /** Number of failures before opening the circuit (default: 3) */
  failureThreshold?: number;
  /** Time in ms to wait before transitioning to HALF_OPEN (default: 30000ms) */
  resetTimeout?: number;
  /** Number of successes in HALF_OPEN before closing the circuit (default: 2) */
  successThreshold?: number;
  /** Maximum jitter in ms to add to reset timeout to prevent thundering herd (default: 5000ms) */
  maxJitter?: number;
  /** Callback invoked when state changes */
  onStateChange?: (name: string, from: CircuitState, to: CircuitState) => void;
}

/**
 * Internal state tracker for the circuit breaker
 */
interface CircuitBreakerState {
  state: CircuitState;
  failures: number;
  successes: number;
  lastFailureTime: number | null;
  nextRetryTime: number | null;
}

/**
 * Error thrown when circuit is open
 */
export class CircuitOpenError extends Error {
  readonly circuitName: string;
  readonly nextRetryTime: number | null;

  constructor(circuitName: string, nextRetryTime: number | null) {
    const retryIn = nextRetryTime ? Math.max(0, nextRetryTime - Date.now()) : null;
    const retryMsg = retryIn !== null ? ` (retry in ${Math.round(retryIn / 1000)}s)` : '';
    super(`Circuit "${circuitName}" is open${retryMsg}`);
    this.name = 'CircuitOpenError';
    this.circuitName = circuitName;
    this.nextRetryTime = nextRetryTime;
  }
}

/**
 * CircuitBreakerService
 *
 * Wraps asynchronous operations with circuit breaker protection to prevent
 * repeated failures from overwhelming external services.
 *
 * Usage:
 * ```ts
 * const circuitBreaker = new CircuitBreakerService({ name: 'my-api' });
 * const result = await circuitBreaker.execute(() => fetchData());
 * ```
 */
export class CircuitBreakerService {
  private readonly name: string;
  private readonly failureThreshold: number;
  private readonly resetTimeout: number;
  private readonly successThreshold: number;
  private readonly maxJitter: number;
  private readonly onStateChange?: (name: string, from: CircuitState, to: CircuitState) => void;

  private internalState: CircuitBreakerState;

  constructor(config: CircuitBreakerConfig) {
    this.name = config.name;
    this.failureThreshold = config.failureThreshold ?? 3;
    this.resetTimeout = config.resetTimeout ?? 30000;
    this.successThreshold = config.successThreshold ?? 2;
    this.maxJitter = config.maxJitter ?? 5000;
    this.onStateChange = config.onStateChange;

    this.internalState = {
      state: CircuitState.CLOSED,
      failures: 0,
      successes: 0,
      lastFailureTime: null,
      nextRetryTime: null,
    };

    Logger.debug(
      `Circuit breaker "${this.name}" initialized (failureThreshold=${this.failureThreshold}, resetTimeout=${this.resetTimeout}ms, successThreshold=${this.successThreshold})`,
      LOG_TAG
    );
  }

  /**
   * Execute an operation with circuit breaker protection
   *
   * @param operation - Async operation to execute
   * @returns Promise resolving to operation result
   * @throws CircuitOpenError if circuit is open
   * @throws Original error if operation fails and circuit trips
   */
  async execute<T>(operation: () => Promise<T>): Promise<T> {
    // Check if we should allow the operation
    if (!this.shouldAllowRequest()) {
      Logger.warn(`Circuit "${this.name}" is OPEN - rejecting request`, LOG_TAG);
      throw new CircuitOpenError(this.name, this.internalState.nextRetryTime);
    }

    try {
      const result = await operation();
      this.recordSuccess();
      return result;
    } catch (error) {
      this.recordFailure();
      throw error;
    }
  }

  /**
   * Execute an operation with fallback when circuit is open
   *
   * @param operation - Async operation to execute
   * @param fallback - Fallback function called when circuit is open
   * @returns Promise resolving to operation result or fallback
   */
  async executeWithFallback<T>(
    operation: () => Promise<T>,
    fallback: () => T | Promise<T>
  ): Promise<T> {
    try {
      return await this.execute(operation);
    } catch (error) {
      if (error instanceof CircuitOpenError) {
        Logger.info(`Circuit "${this.name}" is open - using fallback`, LOG_TAG);
        return fallback();
      }
      throw error;
    }
  }

  /**
   * Get current circuit state
   */
  getState(): CircuitState {
    // Update state based on timing before returning
    this.updateStateIfNeeded();
    return this.internalState.state;
  }

  /**
   * Check if circuit is currently open
   */
  isOpen(): boolean {
    return this.getState() === CircuitState.OPEN;
  }

  /**
   * Check if circuit is closed (fully operational)
   */
  isClosed(): boolean {
    return this.getState() === CircuitState.CLOSED;
  }

  /**
   * Check if circuit is half-open (testing recovery)
   */
  isHalfOpen(): boolean {
    return this.getState() === CircuitState.HALF_OPEN;
  }

  /**
   * Get the name of this circuit breaker
   */
  getName(): string {
    return this.name;
  }

  /**
   * Get current failure count
   */
  getFailureCount(): number {
    return this.internalState.failures;
  }

  /**
   * Get current success count (in half-open state)
   */
  getSuccessCount(): number {
    return this.internalState.successes;
  }

  /**
   * Get time until next retry is allowed (when open)
   * @returns Time in ms, or null if not in open state
   */
  getTimeUntilRetry(): number | null {
    if (this.internalState.nextRetryTime === null) return null;
    const remaining = this.internalState.nextRetryTime - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  /**
   * Manual reset for testing or administrative purposes
   * Resets circuit to CLOSED state
   */
  reset(): void {
    const previousState = this.internalState.state;
    this.internalState = {
      state: CircuitState.CLOSED,
      failures: 0,
      successes: 0,
      lastFailureTime: null,
      nextRetryTime: null,
    };

    if (previousState !== CircuitState.CLOSED) {
      Logger.info(`Circuit "${this.name}" manually reset: ${previousState} → CLOSED`, LOG_TAG);
      this.onStateChange?.(this.name, previousState, CircuitState.CLOSED);
    }
  }

  /**
   * Get detailed stats about the circuit breaker
   */
  getStats(): {
    name: string;
    state: CircuitState;
    failures: number;
    successes: number;
    failureThreshold: number;
    successThreshold: number;
    timeUntilRetry: number | null;
  } {
    return {
      name: this.name,
      state: this.getState(),
      failures: this.internalState.failures,
      successes: this.internalState.successes,
      failureThreshold: this.failureThreshold,
      successThreshold: this.successThreshold,
      timeUntilRetry: this.getTimeUntilRetry(),
    };
  }

  /**
   * Check if request should be allowed based on current state
   */
  private shouldAllowRequest(): boolean {
    this.updateStateIfNeeded();

    switch (this.internalState.state) {
      case CircuitState.CLOSED:
        return true;
      case CircuitState.OPEN:
        return false;
      case CircuitState.HALF_OPEN:
        // In half-open, allow requests to test recovery
        return true;
      default:
        return true;
    }
  }

  /**
   * Update state based on timing (e.g., OPEN → HALF_OPEN after timeout)
   */
  private updateStateIfNeeded(): void {
    if (
      this.internalState.state === CircuitState.OPEN &&
      this.internalState.nextRetryTime !== null &&
      Date.now() >= this.internalState.nextRetryTime
    ) {
      this.transitionTo(CircuitState.HALF_OPEN);
      // Reset success counter for half-open testing
      this.internalState.successes = 0;
    }
  }

  /**
   * Record a successful operation
   */
  private recordSuccess(): void {
    if (this.internalState.state === CircuitState.HALF_OPEN) {
      this.internalState.successes++;
      Logger.debug(
        `Circuit "${this.name}" success in HALF_OPEN (${this.internalState.successes}/${this.successThreshold})`,
        LOG_TAG
      );

      // Check if we've reached success threshold to close the circuit
      if (this.internalState.successes >= this.successThreshold) {
        this.transitionTo(CircuitState.CLOSED);
        this.internalState.failures = 0;
        this.internalState.successes = 0;
        this.internalState.lastFailureTime = null;
        this.internalState.nextRetryTime = null;
      }
    } else if (this.internalState.state === CircuitState.CLOSED) {
      // Reset failure count on success in closed state
      this.internalState.failures = 0;
    }
  }

  /**
   * Record a failed operation
   */
  private recordFailure(): void {
    this.internalState.failures++;
    this.internalState.lastFailureTime = Date.now();

    if (this.internalState.state === CircuitState.HALF_OPEN) {
      // Any failure in half-open immediately opens the circuit
      Logger.warn(`Circuit "${this.name}" failure in HALF_OPEN - reopening`, LOG_TAG);
      this.openCircuit();
    } else if (this.internalState.state === CircuitState.CLOSED) {
      Logger.debug(
        `Circuit "${this.name}" failure (${this.internalState.failures}/${this.failureThreshold})`,
        LOG_TAG
      );

      // Check if we've reached failure threshold
      if (this.internalState.failures >= this.failureThreshold) {
        this.openCircuit();
      }
    }
  }

  /**
   * Open the circuit and set next retry time with jitter
   */
  private openCircuit(): void {
    // Add random jitter to prevent thundering herd
    const jitter = Math.random() * this.maxJitter;
    const nextRetryTime = Date.now() + this.resetTimeout + jitter;

    this.internalState.nextRetryTime = nextRetryTime;
    this.internalState.successes = 0;

    this.transitionTo(CircuitState.OPEN);

    Logger.warn(
      `Circuit "${this.name}" opened after ${this.internalState.failures} failures (retry in ${Math.round((this.resetTimeout + jitter) / 1000)}s)`,
      LOG_TAG
    );
  }

  /**
   * Transition to a new state with logging and callback
   */
  private transitionTo(newState: CircuitState): void {
    const previousState = this.internalState.state;
    if (previousState === newState) return;

    this.internalState.state = newState;

    Logger.info(`Circuit "${this.name}" state change: ${previousState} → ${newState}`, LOG_TAG);
    this.onStateChange?.(this.name, previousState, newState);
  }
}
